import { useCallback, useMemo, useState } from 'react';
import { syncProject, SyncReport, SyncErrorResponse } from 'api/sync';
import { useStore } from 'store/useStore';
import { isErrorResponse } from 'utils/apiUtils';
import useGithubConnection from './useGithubConnection';
import type { SyncButtonState } from 'components/pages/PlanSyncButton';
import type { ExternalItemLink, NodeType, StoryNode } from 'types';

/** Why the Sync button is unavailable, phrased as the action that would fix it. */
const REASON_NOT_READY = 'Checking your GitHub connection…';
const REASON_NOT_CONNECTED = 'Connect GitHub in Settings first';
const REASON_NO_REPO = 'Select a repository in Settings first';

export interface PlanSyncState {
    disabled: boolean;
    disabledReason?: string;
    buttonState: SyncButtonState;
    /** The finished report, for the result modal. Null until a run completes. */
    report: SyncReport | null;
    /** A hard failure (bad token, no repo, sync already running) — not a per-item failure. */
    error: string | null;
    runSync: () => void;
    dismissResult: () => void;
}

/**
 * Owns the Plan page's Sync action: gating, the request, and folding the result back into the
 * store. Lives here rather than in PlanPage.tsx, which is already long and whose subject is the
 * canvas interaction model, not integrations.
 *
 * The link map itself is server-owned — the response is authoritative and simply replaces what
 * the store holds. The one thing written locally is `githubUrl` on the ACTIVE tree's story nodes,
 * so NodePanel's existing GitHub link keeps working; nodes in other trees are not touched,
 * because the client only holds the active tree live and a whole-tree save of a stale snapshot
 * would lose work. Cards in those trees read their link from `activeProject.externalLinks`.
 */
export default function usePlanSync(): PlanSyncState {
    const activeProject = useStore((s) => s.activeProject);
    const setActiveProject = useStore((s) => s.setActiveProject);
    const githubRepo = useStore((s) => s.githubRepo);
    const githubProjectId = useStore((s) => s.githubProjectId);
    const userStories = useStore((s) => s.userStories);
    const updateNode = useStore((s) => s.updateNode);

    const { ready, connected } = useGithubConnection();

    const [running, setRunning] = useState(false);
    const [report, setReport] = useState<SyncReport | null>(null);
    const [error, setError] = useState<string | null>(null);

    // The repo can also be recorded on the project server-side (see api/sync.saveGithubTarget);
    // either source counts as "configured", so a device whose localStorage is empty but whose
    // project already has a target is not blocked.
    const storedRepo = activeProject?.integrations?.github?.repoFullName || '';
    const repoFullName = githubRepo || storedRepo;

    const { disabled, disabledReason } = useMemo(() => {
        if (!activeProject) return { disabled: true, disabledReason: undefined };
        if (!ready) return { disabled: true, disabledReason: REASON_NOT_READY };
        if (!connected) return { disabled: true, disabledReason: REASON_NOT_CONNECTED };
        if (!repoFullName) return { disabled: true, disabledReason: REASON_NO_REPO };
        return { disabled: false, disabledReason: undefined };
    }, [activeProject, ready, connected, repoFullName]);

    /**
     * Mirrors the new issue links onto the active tree's nodes. Only nodes that actually changed
     * are written, so a no-op sync does not dirty the tree and trigger a pointless whole-tree save.
     */
    const applyLinksToActiveTree = useCallback((links: Record<string, ExternalItemLink>) => {
        for (const story of userStories as StoryNode[]) {
            const link = links[story.id];
            if (!link || story.githubUrl === link.url) continue;
            updateNode(story.type as NodeType, story.id, {
                githubUrl: link.url,
                status: 'Pushed to GitHub',
            });
        }
    }, [userStories, updateNode]);

    const runSync = useCallback(async () => {
        if (!activeProject || disabled || running) return;

        setRunning(true);
        setError(null);
        setReport(null);

        const result = await syncProject(activeProject._id, 'github', {
            repoFullName: repoFullName || undefined,
            projectV2Id: githubProjectId || undefined,
        });

        if (isErrorResponse(result)) {
            setError((result as SyncErrorResponse).error);
            setRunning(false);
            return;
        }

        const syncReport = result as SyncReport;
        applyLinksToActiveTree(syncReport.links);

        // Fold the server's authoritative map back into the cached project so the Plan cards'
        // issue chips update without a refetch.
        setActiveProject({
            ...activeProject,
            externalLinks: { items: syncReport.links, relations: activeProject.externalLinks?.relations || {} },
            integrations: {
                ...(activeProject.integrations || {}),
                github: {
                    ...(activeProject.integrations?.github || {}),
                    repoFullName: syncReport.repoFullName,
                    lastSyncAt: syncReport.syncedAt,
                },
            },
        });

        setReport(syncReport);
        setRunning(false);
    }, [activeProject, disabled, running, repoFullName, githubProjectId, applyLinksToActiveTree, setActiveProject]);

    const buttonState: SyncButtonState = running
        ? 'syncing'
        : error || (report && (report.items.failed.length > 0 || report.relations.failed.length > 0))
            ? 'error'
            : report
                ? 'done'
                : 'idle';

    const dismissResult = useCallback(() => {
        setReport(null);
        setError(null);
    }, []);

    return {
        disabled,
        disabledReason,
        buttonState,
        report,
        error,
        runSync: () => { void runSync(); },
        dismissResult,
    };
}
